/**
 * Service for AFIP Casillero Electrónico (WSCCOMU)
 *
 * Calls Netlify Function endpoint. Uses Auth0 token for authentication.
 * Does NOT use Supabase client (AFIP is external, not DB).
 */

import type {
  FiltrosCasillero,
  ResultadoComunicaciones,
  ComunicacionDetalle,
} from '@/types/afip'

const ENDPOINT = '/.netlify/functions/afip-casillero'

/** Error de una respuesta del Casillero, con el código HTTP que la devolvió. */
export class CasilleroError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'CasilleroError'
    this.status = status
  }
}

async function fetchCasillero<T>(
  token: string,
  body: Record<string, unknown>
): Promise<T> {
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  const data = await response.json()

  if (!response.ok) {
    throw new CasilleroError(
      data.error || 'Error consultando casillero electrónico',
      response.status
    )
  }

  return data as T
}

export const casilleroService = {
  async listarComunicaciones(
    token: string,
    cuitContribuyente: string,
    filtros?: FiltrosCasillero
  ): Promise<ResultadoComunicaciones> {
    return fetchCasillero<ResultadoComunicaciones>(token, {
      action: 'listar',
      cuitContribuyente,
      filtros,
    })
  },

  async consumirComunicacion(
    token: string,
    cuitContribuyente: string,
    comunicacionId: number
  ): Promise<ComunicacionDetalle> {
    return fetchCasillero<ComunicacionDetalle>(token, {
      action: 'consumir',
      cuitContribuyente,
      comunicacionId,
    })
  },

  async healthCheck(
    token: string
  ): Promise<{ status: { appserver: string; authserver: string; dbserver: string } }> {
    return fetchCasillero(token, { action: 'dummy' })
  },
}
